/**
 * Map a stored touch record onto the two places it has to land:
 *
 *   - PostHog person properties (snake_case, `AnalyticsUserProperties`)
 *   - the backend payload written onto the User node (camelCase)
 *
 * Kept pure and separate from the React provider so both shapes are derived
 * from one table and can't drift.
 */

import type { AnalyticsUserProperties } from '../analytics/types';

import type { TouchAttribution, TouchSnapshot } from './touch-attribution';

/** snapshot field → PostHog person-property suffix. */
const PERSON_PROPERTY_SUFFIX: ReadonlyArray<[keyof TouchSnapshot, string]> = [
	['gclid', 'gclid'],
	['fbclid', 'fbclid'],
	['utmSource', 'utm_source'],
	['utmMedium', 'utm_medium'],
	['utmCampaign', 'utm_campaign'],
	['utmTerm', 'utm_term'],
	['utmContent', 'utm_content'],
	['referrer', 'referrer'],
	['landingPath', 'landing_path'],
	['at', 'at'],
];

/**
 * Flatten `{ first, last }` into `first_touch_* / last_touch_*` person
 * properties. Empty fields are omitted rather than sent as empty strings, so a
 * person that already has a value never has it blanked by a later write.
 */
export const buildTouchPersonProperties = (
	record: TouchAttribution,
): AnalyticsUserProperties => {
	const properties: Record<string, string> = {};
	for (const [prefix, snapshot] of [
		['first_touch', record.first],
		['last_touch', record.last],
	] as const) {
		for (const [field, suffix] of PERSON_PROPERTY_SUFFIX) {
			const value = snapshot[field];
			if (value) properties[`${prefix}_${suffix}`] = value;
		}
	}
	return properties as AnalyticsUserProperties;
};

/** Wire shape for `POST /api/user/touch-attribution`. */
export interface TouchAttributionPayload {
	first: TouchSnapshot;
	last: TouchSnapshot;
}

export const buildTouchAttributionPayload = (
	record: TouchAttribution,
): TouchAttributionPayload => ({ first: record.first, last: record.last });
